import type { ListResult } from 'pocketbase'
import { ClientResponseError } from 'pocketbase'
import { COL_CRM_USERS } from '../config/auth'
import { COL_EMPLOYEES, COL_ROLES } from '../config/collections'
import { pb } from '../pocketbase'
import { translateErrorMessage } from './pocketbaseErrorMessage'
import { supabase } from './supabase'
import type { EmployeeRecord, RoleRecord } from '../types/records'

const capPerPage = (n: number) => Math.min(Math.max(n, 1), 100)

export type CrmUserRow = {
  id: string
  email: string | null
  name: string | null
  role: string | null
}

/** Приводит запись CRM `users` к виду строки таблицы СУП */
export function mapCrmUserToEmployeeRow(u: CrmUserRow): EmployeeRecord {
  const roleSlug = typeof u.role === 'string' ? u.role.trim() : ''
  const roleName = roleSlug || '—'

  return {
    id: u.id,
    email: typeof u.email === 'string' ? u.email : '',
    name: typeof u.name === 'string' ? u.name : '',
    roleName,
    role: '',
    expand: {},
  } as EmployeeRecord
}

/** Роли PocketBase СУП — справочник прав для админки (не коллекция CRM). */
export async function loadRolesDirectory(): Promise<RoleRecord[]> {
  return pb.collection(COL_ROLES).getFullList<RoleRecord>({ sort: 'name' })
}

/**
 * Подставляет в expand.role запись из коллекции roles СУП по полю roleName,
 * чтобы в таблице показывалось локальное «Название» (title), даже когда список из CRM.
 */
async function enrichRoleTitlesFromSupev(items: EmployeeRecord[]): Promise<void> {
  if (items.length === 0) return
  try {
    const roles = await pb.collection(COL_ROLES).getFullList<RoleRecord>()
    const byName = new Map<string, RoleRecord>()
    for (const r of roles) {
      byName.set(r.name, r)
      const low = r.name.toLowerCase()
      if (!byName.has(low)) byName.set(low, r)
    }
    for (const row of items) {
      const slug = typeof row.roleName === 'string' ? row.roleName.trim() : ''
      if (!slug || slug === '—') continue
      const local = byName.get(slug) ?? byName.get(slug.toLowerCase())
      if (local) {
        row.expand = { ...(row.expand || {}), role: local }
      }
    }
  } catch {
    /* нет доступа к roles или офлайн — колонка остаётся из CRM/без expand */
  }
}

/** Список сотрудников: без sort=-created (у auth часто 400). Пробуем expand, затем без expand + роли. */
export async function loadEmployeesList(
  page = 1,
  perPage = 50,
): Promise<ListResult<EmployeeRecord>> {
  const safePer = capPerPage(perPage)

  if (supabase) {
    const from = (page - 1) * safePer
    const to = from + safePer - 1
    const { data: rows, error, count } = await supabase
      .from(COL_CRM_USERS)
      .select('id, email, name, role', { count: 'exact' })
      .order('email')
      .range(from, to)

    if (error) throw new Error(translateErrorMessage(error.message))

    const total = count ?? rows?.length ?? 0
    const totalPages = Math.max(1, Math.ceil(total / safePer))
    const items = (rows ?? []).map((row) => mapCrmUserToEmployeeRow(row))
    await enrichRoleTitlesFromSupev(items)

    return {
      page,
      perPage: safePer,
      totalItems: total,
      totalPages,
      items,
    } as ListResult<EmployeeRecord>
  }

  async function listWith(opts: { expand?: string; sort?: string }) {
    return pb.collection(COL_EMPLOYEES).getList<EmployeeRecord>(page, safePer, opts)
  }

  let list: ListResult<EmployeeRecord>
  try {
    list = await listWith({ sort: 'email', expand: 'role' })
  } catch (e) {
    if (!(e instanceof ClientResponseError) || e.status !== 400) {
      throw e
    }
    try {
      list = await listWith({ expand: 'role' })
    } catch (e2) {
      if (!(e2 instanceof ClientResponseError) || e2.status !== 400) {
        throw e2
      }
      list = await listWith({})
    }
  }

  if (list.items.some((r) => !r.expand?.role && r.role)) {
    const roles = await pb.collection(COL_ROLES).getFullList<RoleRecord>()
    const byId = new Map(roles.map((r) => [r.id, r]))
    for (const row of list.items) {
      const rid = typeof row.role === 'string' ? row.role : ''
      const role = rid ? byId.get(rid) : undefined
      if (role && !row.expand?.role) {
        row.expand = { ...(row.expand || {}), role }
      }
    }
  }

  await enrichRoleTitlesFromSupev(list.items)
  return list
}
